import { useEffect } from 'react'

import { Field, FieldArray, FieldProps, useFormikContext } from 'formik'

import DefaultButton from 'app/components/buttons/default/component'
import { Box, Flex, TextSpan } from 'app/components/design-system-components'
import { Button } from 'app/components/design-system-components/Button'
import { ButtonSelectGroup } from 'app/components/design-system-components/ButtonSelectGroup'
import { InputField } from 'app/components/design-system-components/inputs'
import { CheckboxInputField } from 'app/components/design-system-components/inputs/Checkboxes/Checkbox'
import * as Display from 'app/components/display/display'
import theme from 'app/match/applicationReportPDF/assets/theme'
import {
  autoFillTenantsFromApplicants,
  createSelectFieldOption,
  formFields,
  isJointTenancySelected,
  JOINT_TENANCY_LABELS,
  JOINT_TENANCY_VALUES,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import {
  CustomMobileInput,
  StyledFieldWrapper,
  StyledFlexBox,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'

// Interface for application applicants data
export interface ApplicationApplicant {
  guidID: string
  firstName: string
  lastName: string
  email: string
  mobileNumber: string
  isPrimary: boolean
}

interface TenantsSectionProps {
  values: RentalHistoryFormValues
  applicants?: ApplicationApplicant[] // New prop for auto-fill functionality
}

const otherTenantOptions = [
  {
    option: JOINT_TENANCY_LABELS.YES,
    optionMeta: {
      id: JOINT_TENANCY_VALUES.YES,
      value: JOINT_TENANCY_VALUES.YES,
    },
  },
  {
    option: JOINT_TENANCY_LABELS.NO,
    optionMeta: {
      id: JOINT_TENANCY_VALUES.NO,
      value: JOINT_TENANCY_VALUES.NO,
    },
  },
]

/**
 * Tenants section component
 * Handles joint tenancy and additional tenant information
 */
export const TenantsSection = ({
  values,
  applicants = [],
}: TenantsSectionProps) => {
  const { setFieldValue, setFieldTouched, setFormikState } =
    useFormikContext<RentalHistoryFormValues>()

  const handleJointTenancyChange = (
    value: typeof otherTenantOptions[number],
    event?: any,
  ) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const label =
      value.optionMeta.value === JOINT_TENANCY_VALUES.YES
        ? JOINT_TENANCY_LABELS.YES
        : JOINT_TENANCY_LABELS.NO

    const optionValue = createSelectFieldOption(value.optionMeta.value, label)

    // Update isJointTenancy field
    setFieldValue(formFields.isJointTenancy, optionValue)
    setFieldTouched(formFields.isJointTenancy, true)
    setFormikState((prev) => ({ ...prev, dirty: true }))

    // Clear tenants if switching to "No"
    if (value.optionMeta.value === JOINT_TENANCY_VALUES.NO) {
      setFieldValue(formFields.tenants, [])
      setFieldTouched(formFields.tenants, true)
    }

    // Force dirty state to enable Save button
    setFormikState((prev) => ({ ...prev, dirty: true }))
  }

  // Auto-fill tenants when joint tenancy is selected and tenant fields are empty
  useEffect(() => {
    const isJointTenancy = isJointTenancySelected(values.isJointTenancy)
    const currentTenants = values.tenants || []

    if (isJointTenancy && applicants.length > 0) {
      const autoFilledTenants = autoFillTenantsFromApplicants(
        applicants,
        currentTenants,
      )
      if (
        autoFilledTenants.length > 0 &&
        autoFilledTenants !== currentTenants
      ) {
        setFieldValue(formFields.tenants, autoFilledTenants)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.isJointTenancy, applicants])

  const currentJointTenancyValue = isJointTenancySelected(values.isJointTenancy)

  // Get the selected option ID for ButtonSelectGroup
  const getSelectedOptionId = (isJointTenancy: boolean): string => {
    return isJointTenancy ? JOINT_TENANCY_VALUES.YES : JOINT_TENANCY_VALUES.NO
  }

  return (
    <Box>
      {/* Joint Tenancy Question */}
      <Box mb={4}>
        <Field name={formFields.isJointTenancy}>
          {() => (
            <ButtonSelectGroup
              label={
                <Box>
                  <TextSpan as="span" fontWeight={theme.fontWeights.bold}>
                    Were there other tenants on this lease?{' '}
                  </TextSpan>
                  <TextSpan as="span" color="red">
                    *
                  </TextSpan>
                </Box>
              }
              selected={getSelectedOptionId(currentJointTenancyValue)}
              selectedVariant="outlineGreen"
              defaultVariant="outlineGray"
              useConnectedStyle={true}
              maxWidth="50%"
              onChange={(
                value: typeof otherTenantOptions[number],
                event?: any,
              ) => {
                handleJointTenancyChange(value, event)
              }}
              options={otherTenantOptions as any}
            />
          )}
        </Field>
      </Box>

      {/* Tenant List - Only show if joint tenancy is selected */}
      {currentJointTenancyValue && (
        <FieldArray name={formFields.tenants}>
          {({ push, remove }) => (
            <Box mb={7}>
              {values.tenants.map((tenant, index) => (
                <Display.GreyContainer
                  key={index}
                  containerClass="mb20 col-sm-12"
                >
                  <Flex mx={5} my={5} flexDirection="column">
                    <div className="field-container row">
                      <div className="col-sm-8">
                        <Display.GreySubheading
                          text={`Tenant ${index + 2} ${
                            tenant.isJointTenant ? '(on the lease)' : ''
                          }`}
                          componentClass="applicant-heading-spacing"
                        >
                          <></>
                        </Display.GreySubheading>
                      </div>
                      <div className="col-sm-4">
                        <DefaultButton
                          text="Remove"
                          componentClass="remove-tenant-record destructive-hollow"
                          size="small"
                          onClick={() => remove(index)}
                          buttonType="destructive"
                        >
                          <></>
                        </DefaultButton>
                      </div>
                    </div>

                    <Box className="tenant-form">
                      {/* Name Fields */}
                      <StyledFlexBox gap={3} mb={7}>
                        <Box flex={1}>
                          <StyledFieldWrapper
                            id={`tenants.${index}.firstName`}
                            name={`tenants.${index}.firstName`}
                            label="First Name"
                            required
                          >
                            <Field name={`tenants.${index}.firstName`}>
                              {({ field }: any) => <InputField {...field} />}
                            </Field>
                          </StyledFieldWrapper>
                        </Box>
                        <Box flex={1}>
                          <StyledFieldWrapper
                            id={`tenants.${index}.lastName`}
                            name={`tenants.${index}.lastName`}
                            label="Last Name"
                            required
                          >
                            <Field name={`tenants.${index}.lastName`}>
                              {({ field }: any) => <InputField {...field} />}
                            </Field>
                          </StyledFieldWrapper>
                        </Box>
                      </StyledFlexBox>

                      <StyledFlexBox gap={3} mb={7}>
                        <Box flex={1}>
                          <StyledFieldWrapper
                            id={`tenants.${index}.mobileNumber`}
                            name={`tenants.${index}.mobileNumber`}
                            label="Mobile Number"
                            required
                          >
                            <Field
                              name={`tenants.${index}.mobileNumber`}
                              as={CustomMobileInput}
                              placeholder={`+61 412 345 678`}
                            />
                          </StyledFieldWrapper>
                        </Box>
                        <Box flex={1}>
                          <StyledFieldWrapper
                            id={`tenants.${index}.email`}
                            name={`tenants.${index}.email`}
                            label="Email"
                            required
                          >
                            <Field name={`tenants.${index}.email`}>
                              {({ field }: any) => (
                                <InputField {...field} type="email" />
                              )}
                            </Field>
                          </StyledFieldWrapper>
                        </Box>
                      </StyledFlexBox>

                      {/* Tenant Type Checkboxes */}
                      <StyledFlexBox className="tenant-type-row">
                        <Field name={`tenants.${index}.isJointTenant`}>
                          {({ field }: FieldProps) => (
                            <CheckboxInputField
                              {...field}
                              value={tenant.isJointTenant}
                              changeHandler={(checked: boolean) => {
                                setFieldValue(
                                  `tenants.${index}.isJointTenant`,
                                  checked,
                                )
                              }}
                            >
                              Tenant (on the lease)
                            </CheckboxInputField>
                          )}
                        </Field>
                        <Field name={`tenants.${index}.isJointTenant`}>
                          {({ field }: FieldProps) => (
                            <CheckboxInputField
                              {...field}
                              value={!tenant.isJointTenant}
                              changeHandler={(checked: boolean) => {
                                setFieldValue(
                                  `tenants.${index}.isJointTenant`,
                                  !checked,
                                )
                              }}
                            >
                              Occupant
                            </CheckboxInputField>
                          )}
                        </Field>
                      </StyledFlexBox>
                    </Box>
                  </Flex>
                </Display.GreyContainer>
              ))}

              {/* Add Another Tenant Button */}
              <Button
                size="lg"
                onClick={() =>
                  push({
                    firstName: '',
                    lastName: '',
                    email: '',
                    mobileNumber: '',
                    isJointTenant: true,
                  })
                }
              >
                Add Another Tenant
              </Button>
            </Box>
          )}
        </FieldArray>
      )}
    </Box>
  )
}
